/**
 * Feb 2026 — Phase 4 of the PWA fluidity plan.
 *
 * Bridges the existing backend SSE event bus into the React Query cache
 * so live server-side changes patch every open surface WITHOUT any
 * additional axios round-trips. Sits at the App shell level so a single
 * SSE subscription covers all tabs.
 *
 * Event → cache mapping (only user-visible events; internal telemetry
 * events like `stream.ready` are ignored):
 *
 *   order.created            → invalidate every orders list
 *   order.status_updated     → patch `['order', id]` if present, invalidate lists
 *   order.assigned           → same as status_updated
 *   order.unassigned         → same
 *   order.items_updated      → same
 *   order.details_updated    → same
 *   order.payment_updated    → same
 *   order.deleted            → remove single, invalidate lists
 *   order.bulk_deleted       → invalidate lists
 *   order.location_updated   → patch `['order', id]` only (do NOT invalidate
 *                              lists — location fires every 5s per active
 *                              order and would trigger a re-fetch storm)
 *   product.updated          → invalidate all products lists
 *   product.stock_changed    → same
 *
 * Design notes:
 *   - `setQueryData(key, updater)` for known-shape patches (instant paint)
 *   - `invalidateQueries` for list-shape changes (background refetch)
 *   - Location updates use `setQueryData` only to avoid a re-fetch every 5s
 *   - Runs only when the user is authenticated. `useSSE` handles reconnect.
 */
import { useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSSE } from './useSSE';
import { useAuth } from '../context/AuthContext';

// List-shape queries that should be invalidated when an order changes.
// Each entry is a queryKey prefix — invalidateQueries matches by prefix.
const ORDER_LIST_KEYS = [
  ['customer', 'orders'],
  ['dispatch', 'orders'],
];
const PRODUCT_LIST_KEYS = [
  ['customer', 'products'],
  ['dispatch', 'products'],
];

export function useSSECacheBridge() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const enabled = !!(user && user.id);

  // Feb 2026 — Latency fix #6, part B. Track whether we've seen a
  // `stream.ready` event before. The FIRST one is the initial connection
  // (page load) — no refetch needed since the useQuery already fired its
  // network request. Every SUBSEQUENT `stream.ready` is a reconnect after
  // the socket dropped (iOS backgrounding, network blip, proxy timeout),
  // meaning we potentially missed events during the gap. Invalidate the
  // list caches so a single fresh fetch fills the hole.
  const hasConnectedRef = useRef(false);

  const patchOrderInCache = useCallback((orderId, patch) => {
    if (!orderId) return;
    queryClient.setQueryData(['order', orderId], (prev) => (
      prev ? { ...prev, ...patch } : prev
    ));
    // Also patch entries in list caches so open list views update the row
    // in-place without a network fetch. Only walk the two known list keys.
    for (const key of ORDER_LIST_KEYS) {
      queryClient.setQueryData(key, (prev) => {
        if (!Array.isArray(prev)) return prev;
        let changed = false;
        const next = prev.map((o) => {
          if (o?.id === orderId) {
            changed = true;
            return { ...o, ...patch };
          }
          return o;
        });
        return changed ? next : prev;
      });
    }
  }, [queryClient]);

  const invalidateOrderLists = useCallback(() => {
    for (const key of ORDER_LIST_KEYS) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }, [queryClient]);

  const invalidateProductLists = useCallback(() => {
    for (const key of PRODUCT_LIST_KEYS) {
      queryClient.invalidateQueries({ queryKey: key });
    }
  }, [queryClient]);

  const removeOrderFromCache = useCallback((orderId) => {
    if (!orderId) return;
    queryClient.removeQueries({ queryKey: ['order', orderId] });
    for (const key of ORDER_LIST_KEYS) {
      queryClient.setQueryData(key, (prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.filter((o) => o?.id !== orderId);
      });
    }
  }, [queryClient]);

  const onEvent = useCallback((ev) => {
    if (!ev || !ev.type) return;
    const t = ev.type;

    // Feb 2026 — SSE reconnect handling. `stream.ready` fires on EVERY
    // successful (re)connection. First one is the initial page-load
    // connection — the useQuery already fetched fresh data, so no-op.
    // Every subsequent one is a reconnect after the socket dropped, meaning
    // one or more events fired while we were disconnected are lost forever
    // (SSE has no per-client replay). Invalidate the list caches so React
    // Query refetches once and reconciles the gap. Single refetch — cheap.
    if (t === 'stream.ready') {
      if (hasConnectedRef.current) {
        invalidateOrderLists();
        invalidateProductLists();
      } else {
        hasConnectedRef.current = true;
      }
      return;
    }

    // Session expired — treat next `stream.ready` as a fresh connection
    // (post re-login), not a reconnect. Prevents a spurious refetch storm.
    if (t === 'stream.session_expired') {
      hasConnectedRef.current = false;
      return;
    }

    // Order events — id can live at ev.order_id, ev.order.id, or ev.id.
    const orderId = ev.order_id || ev.order?.id || ev.id;

    if (t === 'order.created') {
      // Optimistic prepend to list caches so the new order paints instantly
      // WITHOUT a follow-up fetch. Only fall back to an invalidate when the
      // event lacks a full order snapshot (rare — server always attaches it,
      // but stay defensive).
      const created = ev.order;
      if (created && created.id) {
        for (const key of ORDER_LIST_KEYS) {
          queryClient.setQueryData(key, (prev) => {
            if (!Array.isArray(prev)) return prev;
            if (prev.some((o) => o?.id === created.id)) return prev;
            return [created, ...prev];
          });
        }
      } else {
        invalidateOrderLists();
      }
      return;
    }

    if (
      t === 'order.status_updated' ||
      t === 'order.assigned' ||
      t === 'order.unassigned' ||
      t === 'order.items_updated' ||
      t === 'order.details_updated' ||
      t === 'order.payment_updated'
    ) {
      // Server sends the full order snapshot on most of these — patch only.
      // No follow-up invalidate: the whole point of the bridge is to
      // eliminate the network refetch that the operator would otherwise
      // trigger via the polling interval. Fall back to invalidate ONLY
      // when the event is missing enough data to patch cleanly.
      const snapshot = ev.order || null;
      if (snapshot && orderId) {
        patchOrderInCache(orderId, snapshot);
      } else if (orderId) {
        // Bare-bones event with just status/driver_id — patch what we can.
        patchOrderInCache(orderId, {
          status: ev.status,
          driver_id: ev.driver_id,
          updated_at: ev.updated_at,
        });
      } else {
        invalidateOrderLists();
      }
      return;
    }

    if (t === 'order.location_updated') {
      // Location events fire every 5s per active order — patch only, do NOT
      // invalidate lists (would nuke the throttling gain).
      patchOrderInCache(orderId, {
        driver_location: ev.location || {
          lat: ev.latitude,
          lng: ev.longitude,
        },
      });
      return;
    }

    if (t === 'order.deleted') {
      removeOrderFromCache(orderId);
      return;
    }

    if (t === 'order.bulk_deleted') {
      invalidateOrderLists();
      return;
    }

    if (t === 'product.updated' || t === 'product.stock_changed') {
      invalidateProductLists();
      return;
    }

    // Unknown / internal events — ignore.
  }, [queryClient, patchOrderInCache, invalidateOrderLists, invalidateProductLists, removeOrderFromCache]);

  useSSE(onEvent, enabled);
}
